import Decimal from "decimal.js";
import type { DecimalString, MinorUnit, Money } from "./types";

Decimal.set({ precision: 36, rounding: Decimal.ROUND_HALF_EVEN });

export function decimal(value: Decimal.Value | undefined | null): Decimal {
  return new Decimal(value ?? 0);
}

export function toMinor(value: Decimal.Value, scale = 2): MinorUnit {
  return decimal(value).mul(decimal(10).pow(scale)).toDecimalPlaces(0).toFixed(0);
}

export function fromMinor(value: MinorUnit, scale = 2): Decimal {
  return decimal(value).div(decimal(10).pow(scale));
}

export function addMinor(...values: MinorUnit[]): MinorUnit {
  return values.reduce((sum, value) => sum.plus(value), decimal(0)).toFixed(0);
}

export function subtractMinor(left: MinorUnit, right: MinorUnit): MinorUnit {
  return decimal(left).minus(right).toFixed(0);
}

export function multiplyMinor(value: MinorUnit, multiplier: Decimal.Value): MinorUnit {
  return decimal(value).mul(multiplier).toDecimalPlaces(0).toFixed(0);
}

export function money(value: Decimal.Value, currency: string, scale = 2): Money {
  return { minor: toMinor(value, scale), currency: currency.toUpperCase(), scale };
}

export function normalizeDecimal(value: Decimal.Value): DecimalString {
  return decimal(value).toSignificantDigits(24).toString();
}

export function formatMoney(value: MinorUnit, currency: string, scale = 2, locale = "en-US"): string {
  const amount = fromMinor(value, scale).toNumber();
  return new Intl.NumberFormat(locale, { style: "currency", currency, maximumFractionDigits: scale }).format(amount);
}
